import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetCookie, decodeTokens, FetchElements } from "../../utils/js/request";
import { GET_REFRESH_TOKEN, SET_COOKIE, UPDATE_REFRESH_TOKEN, AUTH, SERVICE_ID } from "../../utils/js/Const";

const AuthContext = createContext(null);

const fullLogout = (navigate) => {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    // НЕ делаем navigate здесь
};

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const isRefreshing = useRef(false);
    const isInitialized = useRef(false);

    useEffect(() => {
        try {
            decodeTokens();
        } catch (error) {
            console.error('Ошибка декодирования токена:', error);
            fullLogout(navigate);
        }
    }, [navigate]);

    // Функция для определения пути редиректа на основе ролей
    const getRedirectPath = useCallback((roles) => {
        const roleNames = roles.map(role => role.name);

        // Планограмщики и связанные роли -> на страницу планограмм
        if (roleNames.includes("Планограмщик") ||
            roleNames.includes("Обычный Планограмщик") ||
            roleNames.includes("Технолог ГВ") ||
            roleNames.includes("Главный технолог ГВ")) {
            return "/PlannogrammList";
        }

        // Остальные пользователи -> на главную
        return "/main";
    }, []);

    // Функция обновления токена
    const refreshTokens = useCallback(async () => {
        const refreshToken = GET_REFRESH_TOKEN();

        if (!refreshToken || refreshToken === 'null' || refreshToken === 'undefined' || refreshToken === '') {
            return false;
        }

        try {
            const response = await fetch(`${AUTH}/api/token/refresh?service_id=${SERVICE_ID}&device=web`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            const result = await response.json();

            if (response.ok) {
                const newAccessToken = result.data.access_token;
                const newRefreshToken = result.data.refresh_token;

                SET_COOKIE("access_token", newAccessToken.value, newAccessToken.max_age);
                UPDATE_REFRESH_TOKEN(newRefreshToken.value);
                return true;
            } else {
                console.error('Ошибка обновления токена:', result);
                return false;
            }
        } catch (error) {
            console.error('Сетевая ошибка при обновлении токена:', error);
            return false;
        }
    }, []);

    const checkAuth = useCallback(async () => {
        // Предотвращаем повторные вызовы
        if (isRefreshing.current || isInitialized.current) {
            return;
        }

        isRefreshing.current = true;
        setLoading(true);

        let token = GetCookie("access_token");
        const refreshToken = GET_REFRESH_TOKEN();

        // Если нет access_token, но есть refresh_token - пробуем обновить
        if (!token && refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined') {
            const refreshed = await refreshTokens();
            if (refreshed) {
                token = GetCookie("access_token");
                try {
                    decodeTokens();
                } catch (error) {
                    console.error('Ошибка декодирования обновленного токена:', error);
                }
            } else {
                fullLogout(navigate);
                setLoading(false);
                isRefreshing.current = false;
                if (window.location.pathname !== '/auth') {
                    navigate('/auth');
                }
                return;
            }
        }

        // Если есть токен - загружаем пользователя
        if (token) {
            const savedLogin = localStorage.getItem('USER_LOGIN');
            const savedFullName = localStorage.getItem('USER_FULL_NAME');
            const savedGuid = localStorage.getItem('USER_GUID');
            const savedRoles = localStorage.getItem('USER_ROLES');

            if (savedLogin) {
                const parsedRoles = savedRoles ? JSON.parse(savedRoles) : [];
                setUser({
                    guid: savedGuid,
                    login: savedLogin,
                    fullName: savedFullName,
                    roles: parsedRoles
                });
                setUserRoles(parsedRoles);

                if (parsedRoles.length > 0) {
                    localStorage.setItem('ROLE', parsedRoles[0].name);
                }

                setLoading(false);
                isRefreshing.current = false;
                isInitialized.current = true;

                // Редирект только если на странице авторизации или корне
                const currentPath = window.location.pathname;
                if (currentPath === '/auth' || currentPath === '/') {
                    const redirectPath = getRedirectPath(parsedRoles);
                    navigate(redirectPath, { replace: true });
                }
                return;
            }
        }

        // Нет валидной сессии
        setUser(null);
        setUserRoles([]);
        setLoading(false);
        isRefreshing.current = false;
        isInitialized.current = false;

        // Если не на странице входа - редиректим туда
        if (window.location.pathname !== '/auth') {
            navigate('/auth', { replace: true });
        }
    }, [refreshTokens, navigate, getRedirectPath]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (formData) => {
        try {
            const response = await fetch(`${AUTH}/api/v1/login?services_guid=${SERVICE_ID}&device=web`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (response.ok) {
                const userData = result.data.user;
                const userRolesData = userData.roles || [];
                const accessTokenData = result.data.access_token;
                const refreshTokenData = result.data.refresh_token;

                SET_COOKIE("access_token", accessTokenData.value, accessTokenData.max_age);
                UPDATE_REFRESH_TOKEN(refreshTokenData.value);

                setUser({
                    guid: userData.guid,
                    login: userData.extended_object?.login,
                    fullName: userData.extended_object?.full_name,
                    shopId: userData.extended_object?.shop_id,
                    roles: userRolesData
                });

                setUserRoles(userRolesData);

                localStorage.setItem('USER_LOGIN', userData.extended_object?.login || '');
                localStorage.setItem('USER_FULL_NAME', userData.extended_object?.full_name || '');
                localStorage.setItem('USER_GUID', userData.guid || '');
                localStorage.setItem('USER_ROLES', JSON.stringify(userRolesData));

                if (userRolesData.length > 0) {
                    localStorage.setItem('ROLE', userRolesData[0].name);
                }

                try {
                    decodeTokens();
                } catch (error) {
                    console.error('Ошибка декодирования токена после логина:', error);
                }

                isInitialized.current = true;

                // Редирект после успешного входа
                const redirectPath = getRedirectPath(userRolesData);
                navigate(redirectPath, { replace: true });

                return { success: true, user: userData, roles: userRolesData };
            } else {
                return { success: false, error: result.message || 'Ошибка авторизации' };
            }
        } catch (error) {
            console.error('Ошибка при входе:', error);
            return { success: false, error: error.message };
        }
    }, [navigate, getRedirectPath]);

    const logout = useCallback(() => {
        fullLogout(navigate);
        isInitialized.current = false;
        setUser(null);
        setUserRoles([]);
        navigate('/auth', { replace: true });
    }, [navigate]);

    const hasRole = useCallback((roleName) => {
        return userRoles.some(role => role.name === roleName);
    }, [userRoles]);

    const hasAnyRole = useCallback((roleNames) => {
        return userRoles.some(role => roleNames.includes(role.name));
    }, [userRoles]);

    // Используем useMemo для value, чтобы предотвратить лишние рендеры
    const value = useMemo(() => ({
        user,
        userRoles,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        checkAuth,
        getRedirectPath,
        hasRole,
        hasAnyRole,
        setUserRoles,
        refreshTokens
    }), [user, userRoles, loading, login, logout, checkAuth, getRedirectPath, hasRole, hasAnyRole, refreshTokens]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};